"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ANIMALIA_TOKEN_VALUES } from "../tokens";
import AniButton from "../primitives/AniButton";
import AniCard from "../primitives/AniCard";
import AniIcon from "../primitives/AniIcon";
import AniLoadingIcon from "../primitives/AniLoadingIcon";
import AniTag from "../primitives/AniTag";

type MascotId = "panther" | "lizard" | "onca";

/** Animalia's mascots, in the order Figma node 199:4479 lists them. */
const MASCOTS: ReadonlyArray<{ id: MascotId; name: string; role: string; image: string }> = [
  { id: "panther", name: "Panther Jr.", role: "Request support", image: "mascot-panther-jr.jpg" },
  { id: "lizard", name: "Mr. Lizard", role: "Icon specialist", image: "mascot-mr-lizard.jpg" },
  { id: "onca", name: "Onca", role: "Component support", image: "mascot-onca.jpg" },
];

/**
 * The mocked AI. A rule fires when one of its terms starts a word of the
 * request (accents and case ignored), adding its tag and the mascots that
 * answer it. A request no rule understands answers nothing, and on purpose
 * shows nothing: Ani Search AI has no empty state.
 */
const RULES: ReadonlyArray<{ tag: string; terms: string[]; mascots: MascotId[] }> = [
  { tag: "agent", terms: ["agent"], mascots: ["panther", "lizard", "onca"] },
  { tag: "animalia", terms: ["animalia"], mascots: ["panther", "lizard", "onca"] },
  { tag: "support", terms: ["support"], mascots: ["panther", "onca"] },
  { tag: "Panther Jr.", terms: ["request", "panther", "junior", "jr"], mascots: ["panther"] },
  { tag: "icons", terms: ["icon", "specialist"], mascots: ["lizard"] },
  { tag: "Mr. Lizard", terms: ["lizard"], mascots: ["lizard"] },
  { tag: "Onca", terms: ["onca"], mascots: ["onca"] },
];

const INITIAL_TAGS = ["agent", "animalia"];
/** About what po-search-ai takes to answer, and long enough for one full shimmer pass. */
const PROCESSING_MS = 1800;

const normalize = (value: string) =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function interpret(request: string) {
  const words = normalize(request).split(/[^a-z0-9]+/).filter(Boolean);
  return RULES.filter((rule) =>
    rule.terms.some((term) => words.some((word) => word.startsWith(term))),
  ).map((rule) => rule.tag);
}

const answer = (tags: string[]) =>
  MASCOTS.filter((mascot) =>
    RULES.some((rule) => tags.includes(rule.tag) && rule.mascots.includes(mascot.id)),
  );

const countLabel = (count: number) => `${count} result${count === 1 ? "" : "s"}`;

/**
 * Behaves like PO UI's po-search-ai: Enter or the wand sends the request, the
 * field locks and shimmers while a loading ring stands in for its actions,
 * and the request stays in the field — with a clear action — once answered.
 */
const AniSearch = () => {
  const [query, setQuery] = useState("");
  const [tags, setTags] = useState(INITIAL_TAGS);
  const [processing, setProcessing] = useState(false);
  // Bumped per answered request, so its tags and cards replay their entrance.
  const [revision, setRevision] = useState(0);
  const [status, setStatus] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const results = answer(tags);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const request = query.trim();
    if (!request || processing) return;
    // The wand gives way to the loading ring, so focus moves to the field first.
    inputRef.current?.focus();
    setProcessing(true);
    setStatus("Processing your request…");
    timer.current = window.setTimeout(() => {
      const next = interpret(request);
      setTags(next);
      setRevision((value) => value + 1);
      setProcessing(false);
      setStatus(`${countLabel(answer(next).length)} for ${request}`);
    }, PROCESSING_MS);
  };

  const clear = () => {
    setQuery("");
    inputRef.current?.focus();
  };

  const removeTag = (tag: string) => {
    const next = tags.filter((item) => item !== tag);
    setTags(next);
    setStatus(`${tag} removed. ${countLabel(answer(next).length)}`);
  };

  return (
    <div className="ani-search">
      <form
        role="search"
        data-token="Ani Search AI/Trigger"
        data-pad="xs"
        data-inspect-tokens="--ani-search-background --ani-search-border-color --ani-search-processing-background --ani-search-processing-color"
        data-processing={processing || undefined}
        className="ani-search-field ani-tint"
        onSubmit={submit}
      >
        <AniIcon name="figma-search" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder="Search"
          readOnly={processing}
          autoComplete="off"
          enterKeyHint="search"
          aria-label="Search the Animalia library"
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="ani-search-trailing">
          {processing ? (
            <span className="ani-search-processing-icon">
              <AniLoadingIcon />
            </span>
          ) : (
            <>
              {query && (
                <>
                  <button type="button" className="ani-search-clear" aria-label="Clear search" onClick={clear}>
                    <AniIcon name="figma-x-circle-fill" />
                  </button>
                  <span className="ani-search-divider" aria-hidden="true" />
                </>
              )}
              <button type="submit" className="ani-search-action" aria-label="Ask Animalia AI">
                <AniIcon name="magic-wand" />
              </button>
            </>
          )}
        </div>
      </form>

      <div
        className="ani-search-tags"
        data-structure="Search tags"
        data-gap="spacing/xxs"
        data-animate={revision ? "" : undefined}
      >
        {tags.map((tag) => (
          <AniTag key={`${revision}-${tag}`} removeLabel={`Remove ${tag} tag`} onRemove={() => removeTag(tag)}>
            {tag}
          </AniTag>
        ))}
      </div>

      <ul
        className="ani-search-results"
        aria-busy={processing}
        data-structure="Search results"
        data-gap="spacing/xs"
        data-animate={revision ? "" : undefined}
      >
        {results.map((mascot, index) => (
          <li key={`${revision}-${mascot.id}`} style={{ "--ani-search-order": index } as React.CSSProperties}>
            <AniCard className="ani-search-card">
              <div className="ani-search-mascot" data-gap="spacing/md">
                <span className="ani-search-avatar" data-token="Ani Avatar/Photo">
                  <Image
                    src={`/images/animalia/playground/${mascot.image}`}
                    alt=""
                    width={ANIMALIA_TOKEN_VALUES.mascotSize}
                    height={ANIMALIA_TOKEN_VALUES.mascotSize}
                  />
                </span>
                <p className="ani-search-identity">
                  <strong>{mascot.name}</strong>
                  <span>{mascot.role}</span>
                </p>
                <AniButton variant="tertiary" iconOnly aria-label={`Chat with ${mascot.name}`}>
                  <AniIcon name="chat-circle" />
                </AniButton>
              </div>
            </AniCard>
          </li>
        ))}
      </ul>

      <p className="sr-only" role="status">{status}</p>
    </div>
  );
};

export default AniSearch;
